import { getGiftPurchaserAndRecipient, PurchaseActionOption } from "common";
import intersectionBy from "lodash/intersectionBy";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import {
    CouponTerm,
    CouponTermType,
    GiftPurchaseItem,
    Product,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
} from "types";
import { useSeller } from "../../hooks/useAccount";
import { useCollection } from "../../hooks/useCollection";
import giftBannerBackground from "../../images/giftBannerBackground.png";
import { Currency } from "../Common/Currency";
import { GiftPurchaseSummary } from "./GiftPurchaseSummary";

interface GiftReceiptToPrintProps {
    purchase: Purchase;
    action: PurchaseActionOption | null;
    onClosePrintDialog: () => void;
    item?: GiftPurchaseItem;
}

export const GiftReceiptToPrint = ({ purchase, action, item: gift, onClosePrintDialog }: GiftReceiptToPrintProps) => {
    const selectedGift = gift
        ? gift
        : (purchase.items.find((item: PurchaseItem) => item.object === PurchaseItemObject.GIFT) as GiftPurchaseItem);
    const { data: products } = useCollection<Product>("products");
    const componentRef = useRef(null);
    const [show, setShow] = useState(false);
    const seller = useSeller();

    const reactToPrintContent = useCallback(() => {
        return componentRef.current;
    }, [componentRef]);

    const handleAfterPrint = useCallback(() => {
        onClosePrintDialog();
        setShow(false);
    }, [onClosePrintDialog, setShow]);

    const handleOnBeforeGetContent = useCallback(async () => {
        setShow(true);
        return null;
    }, []);

    const handlePrint = useReactToPrint({
        content: reactToPrintContent,
        documentTitle: "Gift Certificate",
        onBeforeGetContent: handleOnBeforeGetContent,
        onAfterPrint: handleAfterPrint,
        removeAfterPrint: true,
    });

    useEffect(() => {
        if (action === PurchaseActionOption.PRINT_GIFT_CERTIFICATE) {
            handlePrint();
        }
    }, [action, handlePrint]);

    if (!show || !selectedGift) {
        return null;
    }

    const { from, to } = getGiftPurchaserAndRecipient(purchase, selectedGift);

    const giftTerms = selectedGift.coupon?.terms ?? [];

    return (
        <div className="absolute hidden">
            {/* This component should be in the DOM but kept out of eyes */}
            <div ref={componentRef} className="w-full">
                <div style={{ height: 170, background: `url(${giftBannerBackground}) 50% 10%/cover` }} />

                <div className="px-20 pt-9">
                    <div className="space-y-6 text-center">
                        {purchase.customerName && (
                            <div className="text-2xl font-bold">A gift from {purchase.customerName}</div>
                        )}

                        <GiftPurchaseSummary purchase={purchase} item={selectedGift} />

                        <div className="text-[48px] font-bold">
                            <Currency>{selectedGift.balance}</Currency>
                        </div>
                    </div>

                    <div className="text-center">
                        <div className="mt-2 inline-flex items-center space-x-2 rounded border-2 border-dashed border-gray px-3 py-1.5">
                            <div className="text-md">Gift Code</div>
                            <div className="text-xl font-bold uppercase">{selectedGift.couponCode?.code}</div>
                        </div>
                    </div>

                    <div className="mt-8 space-y-1">
                        {to && (
                            <div>
                                <span className="mr-1 font-bold">To:</span>
                                <var data-var="recipient">{to}</var>
                            </div>
                        )}
                        {from && (
                            <div>
                                <span className="mr-1 font-bold">From:</span>
                                <var data-var="sender">{from}</var>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 space-y-6">
                        {selectedGift.message && selectedGift.message.length > 0 && (
                            <div className="space-y-1">
                                <div className="font-bold">Message:</div>
                                <div>{selectedGift.message.trim()}</div>
                            </div>
                        )}

                        <div className="space-y-1">
                            <div className="font-bold">Instructions:</div>
                            <div>
                                You can redeem this gift on the {seller.name} website. Fill out your details and on the
                                payment screen, click the <strong>Add a code</strong> link and enter your gift code{" "}
                                <span className="font-bold uppercase">{selectedGift.couponCode?.code}</span>. The value
                                of this gift certificate will then be applied to your purchase.
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="font-bold">Product Restrictions:</div>
                            <div>{getProductRestrictions(products ?? [], giftTerms)}</div>
                        </div>

                        <div className="space-y-1">
                            <div className="font-bold">Arrival Restrictions:</div>
                            <div>{getArrivalRestrictions(giftTerms) ?? "None"}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const getProductRestrictions = (allProducts: Product[], terms: CouponTerm[]) => {
    const productsTerm = terms.find((term) => term.type === CouponTermType.PRODUCTS);
    const termWithId = (productsTerm?.products ?? []).map((id) => ({ id }));
    const products = intersectionBy(allProducts, termWithId, "id");
    return products.map((product) => product.name).join(", ");
};

const getArrivalRestrictions = (terms: CouponTerm[]) => {
    return terms.find((term) => term.type === CouponTermType.ARRIVAL_WINDOW)?.caption;
};
